/**
 * Browse filters live in sessionStorage so item detail can return to the same
 * results. They must clear when the user leaves that browse "family" (e.g. Home),
 * and only stick when staying in browse ↔ detail.
 */

export const MARKETPLACE_BROWSE_STATE_KEY = 'marketplaceBrowseState';

const MY_VAULT_BROWSE_KEYS = [
    'userCollectiblesSearchState',
    'userCollectiblesPendingRestore',
    'userDiecastsSearchState',
    'userDiecastsPendingRestore',
    'userHeroCardsSearchState',
];

const PUBLIC_COLLECTIBLES_BROWSE_KEYS = [
    'collectiblesSearchState',
    'collectiblesPendingRestore',
    'diecastsSearchState',
    'diecastsPendingRestore',
];

const PUBLIC_HERO_CARDS_BROWSE_KEYS = [
    'heroCardsSearchState',
];

const WISHLIST_BROWSE_KEYS = [
    'userWishListSearchState',
];

const PUBLIC_USER_KEY_PREFIXES = [
    'publicUserCollectiblesSearchState_',
    'publicUserCollectiblesPendingRestore_',
    'publicUserDiecastsSearchState_',
    'publicUserDiecastsPendingRestore_',
    'publicUserHeroCardsSearchState_',
    'publicUserHeroCardsPendingRestore_',
];

function removeKeys(keys) {
    keys.forEach((key) => sessionStorage.removeItem(key));
}

function removeKeysWithPrefixes(prefixes) {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
            toRemove.push(key);
        }
    }
    toRemove.forEach((key) => sessionStorage.removeItem(key));
}

export function clearMarketplaceBrowseSession() {
    sessionStorage.removeItem(MARKETPLACE_BROWSE_STATE_KEY);
}

export function clearMyVaultBrowseSession() {
    removeKeys(MY_VAULT_BROWSE_KEYS);
}

export function clearPublicCollectiblesBrowseSession() {
    removeKeys(PUBLIC_COLLECTIBLES_BROWSE_KEYS);
}

export function clearPublicHeroCardsBrowseSession() {
    removeKeys(PUBLIC_HERO_CARDS_BROWSE_KEYS);
}

export function clearWishlistBrowseSession() {
    removeKeys(WISHLIST_BROWSE_KEYS);
}

export function clearPublicUserBrowseSession(publicUserId) {
    if (publicUserId) {
        PUBLIC_USER_KEY_PREFIXES.forEach((prefix) => {
            sessionStorage.removeItem(`${prefix}${publicUserId}`);
        });
        return;
    }
    removeKeysWithPrefixes(PUBLIC_USER_KEY_PREFIXES);
}

/** Clears vault/search/wishlist keys (used when switching item type tabs). */
export function clearVaultSearchSession(publicUserId) {
    clearMyVaultBrowseSession();
    clearPublicCollectiblesBrowseSession();
    clearPublicHeroCardsBrowseSession();
    clearWishlistBrowseSession();
    clearPublicUserBrowseSession(publicUserId);
}

/**
 * Drop browse filter snapshots when the user leaves that section's route family.
 * Detail routes keep their parent family's keys so Back can restore.
 */
export function syncBrowseSessionToPathname(pathname) {
    const p = (pathname || '').toLowerCase();

    const onMarketplaceBrowse = p === '/marketplace' || p.startsWith('/marketplaceitem/');
    const onMyItems = p === '/myitems';
    const onCollectionItem = p.startsWith('/collectionitem/');
    const onHeroCard = p.startsWith('/herocard/');
    const onWishlist = p === '/myherocardwishlist';
    const onSearchCollectibles = p === '/searchcollectibles' || p === '/searchdiecasts';
    const onSearchHeroCards = p === '/searchherocards';
    const onPublicUser = p.startsWith('/searchuserscollection/');

    if (!onMarketplaceBrowse) {
        clearMarketplaceBrowseSession();
    }

    if (!onMyItems && !onCollectionItem && !onHeroCard) {
        clearMyVaultBrowseSession();
    }

    if (!onSearchCollectibles && !onCollectionItem) {
        clearPublicCollectiblesBrowseSession();
    }

    if (!onSearchHeroCards && !onHeroCard) {
        clearPublicHeroCardsBrowseSession();
    }

    if (!onWishlist && !onCollectionItem && !onHeroCard && !onMarketplaceBrowse) {
        clearWishlistBrowseSession();
    }

    if (!onPublicUser && !onCollectionItem && !onHeroCard) {
        clearPublicUserBrowseSession();
    }
}
